import React, { useState, useEffect } from 'react';
import { useAlert } from '../../providers/AlertProvider';
import { getOrderPaperwork, getPaperworkFileUrl, deleteCustomerPaperwork, getAllPaperworkUrls } from '../../api/paperwork';
import PDFViewer from './PDFViewer';
import logger from "../../utils/logger";

const CustomerPaperworkViewer = ({ 
    orderId, 
    orderReference,
    orderStatus,
    theme = 'light', 
    onPaperworkChanged 
}) => {
    const { showAlert } = useAlert();
    const [paperwork, setPaperwork] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedPdf, setSelectedPdf] = useState(null);
    const [showPdfModal, setShowPdfModal] = useState(false);

    const isDark = theme === 'dark';
    
    // Status-based permissions
    // Delete allowed for: pending, picked (not dispatched or later)
    const canDelete = orderStatus === 'pending' || orderStatus === 'picked';

    useEffect(() => {
        if (orderId) {
            loadPaperwork();
        }
    }, [orderId]);

    const loadPaperwork = async () => {
        setLoading(true);
        setError(null);
        
        try {
            const response = await getOrderPaperwork(orderId);
            
            if (response.success) {
                setPaperwork(response.data);
            } else {
                // Don't show errors for missing paperwork - it's normal for new orders
                logger.log('No paperwork found for order:', response.message);
                setPaperwork([]);
            }
        } catch (err) {
            logger.error('Error loading paperwork:', err);
            // Don't show error to user - just assume no paperwork exists
            setPaperwork([]);
        } finally {
            setLoading(false);
        }
    };

    const handleViewPdf = async (paperworkItem) => {
        try {
            const response = await getPaperworkFileUrl(paperworkItem.file_path);
            logger.log('PDF URL response:', response.fileUrl);
            if (response.success) {
                setSelectedPdf(response.fileUrl);
                setShowPdfModal(true);
            } else {
                showAlert({
                    message: response.message || 'Failed to load PDF',
                    type: 'danger',
                    buttons: [{ label: 'OK', type: 'primary' }]
                });
            }
        } catch (error) {
            logger.error('Error getting PDF URL:', error);
            showAlert({
                message: 'Error loading PDF file',
                type: 'danger',
                buttons: [{ label: 'OK', type: 'primary' }]
            });
        }
    };

    const handleDeletePdf = async (paperworkItem) => {
        showAlert({
            message: `Are you sure you want to delete "${paperworkItem.filename}"? This action cannot be undone.`,
            type: 'warning',
            buttons: [
                {
                    label: 'Delete',
                    type: 'danger',
                    onClick: async () => {
                        try {
                            const response = await deleteCustomerPaperwork(paperworkItem.id);
                            
                            if (response.success) {
                                showAlert({
                                    message: 'Paperwork deleted successfully',
                                    type: 'success',
                                    buttons: [{ label: 'OK', type: 'primary' }]
                                });
                                
                                // Reload paperwork list
                                loadPaperwork();
                                
                                // Notify parent component
                                if (onPaperworkChanged) {
                                    onPaperworkChanged();
                                }
                            } else {
                                showAlert({
                                    message: response.message || 'Failed to delete paperwork',
                                    type: 'danger',
                                    buttons: [{ label: 'OK', type: 'primary' }]
                                });
                            }
                        } catch (error) {
                            logger.error('Error deleting paperwork:', error);
                            showAlert({
                                message: 'Error deleting paperwork',
                                type: 'danger',
                                buttons: [{ label: 'OK', type: 'primary' }]
                            });
                        }
                    }
                },
                {
                    label: 'Cancel',
                    type: 'secondary'
                }
            ]
        });
    };

    const handlePrintAll = async () => {
        try {
            const response = await getAllPaperworkUrls(orderId);
            
            if (response.success && response.data.length > 0) {
                showAlert({
                    message: `Downloading ${response.data.length} PDF${response.data.length > 1 ? 's' : ''} to Downloads folder...`,
                    type: 'info',
                    buttons: [{ label: 'OK', type: 'primary' }]
                });

                // Download each PDF using the same method as PDFViewer
                for (let i = 0; i < response.data.length; i++) {
                    const paperworkItem = response.data[i];
                    
                    try {
                        // Create filename using order reference
                        const fileExtension = paperworkItem.filename ? 
                            paperworkItem.filename.split('.').pop() : 'pdf';
                        const downloadFilename = response.data.length > 1 ? 
                            `${orderReference || `Order-${orderId}`}_paperwork_${i + 1}.${fileExtension}` :
                            `${orderReference || `Order-${orderId}`}_paperwork.${fileExtension}`;
                        
                        // Use fetch API to download like in PDFViewer
                        fetch(paperworkItem.file_url)
                            .then(response => response.blob())
                            .then(blob => {
                                const a = document.createElement('a');
                                a.href = URL.createObjectURL(blob);
                                a.download = downloadFilename;
                                document.body.appendChild(a);
                                a.click();
                                document.body.removeChild(a);
                                
                                // Clean up the object URL
                                setTimeout(() => URL.revokeObjectURL(a.href), 100);
                            })
                            .catch(error => {
                                logger.error(`Error downloading PDF ${paperworkItem.filename}:`, error);
                            });
                        
                        // Small delay between downloads to prevent browser blocking
                        if (i < response.data.length - 1) {
                            await new Promise(resolve => setTimeout(resolve, 1000));
                        }
                        
                    } catch (error) {
                        logger.error(`Error processing PDF ${paperworkItem.filename}:`, error);
                    }
                }
                
                // Show completion message
                setTimeout(() => {
                    showAlert({
                        message: `${response.data.length} PDF${response.data.length > 1 ? 's have' : ' has'} been downloaded to your Downloads folder.`,
                        type: 'success',
                        buttons: [{ label: 'OK', type: 'primary' }]
                    });
                }, 2000);
                
            } else {
                showAlert({
                    message: response.message || 'No paperwork found to download',
                    type: 'warning',
                    buttons: [{ label: 'OK', type: 'primary' }]
                });
            }
        } catch (error) {
            logger.error('Error downloading all paperwork:', error);
            showAlert({
                message: 'Error downloading paperwork',
                type: 'danger',
                buttons: [{ label: 'OK', type: 'primary' }]
            });
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return '-';
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const buttonStyle = {
        padding: '0.25rem 0.5rem',
        margin: '0.125rem',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '0.8rem',
        fontWeight: 'bold'
    };

    const primaryButtonStyle = {
        ...buttonStyle,
        backgroundColor: '#007bff',
        color: '#fff'
    };

    const dangerButtonStyle = {
        ...buttonStyle,
        backgroundColor: '#dc3545',
        color: '#fff'
    };

    const successButtonStyle = {
        ...buttonStyle,
        backgroundColor: '#28a745',
        color: '#fff'
    };

    if (loading) {
        return (
            <div style={{ 
                padding: '2rem', 
                textAlign: 'center', 
                color: isDark ? '#fff' : '#000' 
            }}>
                Loading paperwork...
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ 
                padding: '1rem', 
                color: '#dc3545',
                border: '1px solid #dc3545',
                borderRadius: '4px',
                backgroundColor: isDark ? '#2a1f1f' : '#f8d7da'
            }}>
                {error}
            </div>
        );
    }

    if (paperwork.length === 0) {
        return (
            <div style={{ 
                padding: '1rem', 
                textAlign: 'center',
                color: isDark ? '#ccc' : '#666',
                backgroundColor: isDark ? '#2a2a2a' : '#f8f9fa',
                border: `1px solid ${isDark ? '#555' : '#ddd'}`,
                borderRadius: '4px'
            }}>
                No paperwork uploaded for this order
            </div>
        );
    }

    return (
        <div>
            <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '1rem'
            }}>
                <h4 style={{ 
                    margin: 0, 
                    color: isDark ? '#fff' : '#000' 
                }}>
                    Customer Paperwork ({paperwork.length})
                </h4>
                
                {/* Download All button - only show if paperwork exists */}
                {paperwork.length > 0 && (
                    <button
                        onClick={handlePrintAll}
                        style={successButtonStyle}
                        title="Download all paperwork for this order"
                    >
                        � Download All ({paperwork.length})
                    </button>
                )}
            </div>
            
            <div style={{ 
                border: `1px solid ${isDark ? '#555' : '#ddd'}`,
                borderRadius: '4px',
                backgroundColor: isDark ? '#2a2a2a' : '#fff'
            }}>
                {paperwork.map((item, index) => (
                    <div
                        key={item.id}
                        style={{
                            padding: '1rem',
                            borderBottom: index < paperwork.length - 1 ? `1px solid ${isDark ? '#555' : '#eee'}` : 'none',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            color: isDark ? '#fff' : '#000'
                        }}
                    >
                        <div style={{ flex: 1 }}>
                            <div style={{ 
                                fontWeight: 'bold', 
                                fontSize: '1rem',
                                marginBottom: '0.25rem'
                            }}>
                                📄 {item.filename}
                            </div>
                            <div style={{ 
                                fontSize: '0.85rem', 
                                opacity: 0.7,
                                display: 'grid',
                                gridTemplateColumns: 'auto auto auto',
                                gap: '1rem',
                                alignItems: 'center',
                                justifyContent: 'start'
                            }}>
                                <span>Size: {formatFileSize(item.file_size)}</span>
                                <span>Uploaded: {formatDate(item.upload_date)}</span>
                                {item.uploaded_by && (
                                    <span>By: {item.uploaded_by}</span>
                                )}
                            </div>
                        </div>
                        
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button
                                onClick={() => handleViewPdf(item)}
                                style={primaryButtonStyle}
                                title="View PDF"
                            >
                                View
                            </button>
                            
                            {canDelete && (
                                <button
                                    onClick={() => handleDeletePdf(item)}
                                    style={dangerButtonStyle}
                                    title="Delete PDF"
                                >
                                    Delete
                                </button>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/* PDF Viewer Modal */}
            {showPdfModal && selectedPdf && (
                <PDFViewer 
                    pdfUrl={selectedPdf} 
                    show={setShowPdfModal} 
                />
            )}
        </div>
    );
};

export default CustomerPaperworkViewer;
